import { faMinus, faPlus, faPlusSquare, faRemove } from "@fortawesome/free-solid-svg-icons"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { addtionalMemory } from "../../Objects/Memory/Memory"
import { OrderCatalogProductAdditionalEntity } from "../../Objects/Order/OrderCatalogProductAdditionalEntity"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { ButtonIcon } from "../Button/Button"
import InputField from "../InputField/InputField"
import type { ListItem } from "../List/List"
import ModalPortal from "../Modal/ModalPortal"
import { TitleChildrenPath } from "../Titles/Titles"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

interface AdditionalSelectionModalProps {
  listCards: ListItem[]
  selectedAdditionals: OrderCatalogProductAdditionalEntity[]
  isStockLimitReached: (additionalId: number, currentAmount: number) => boolean
  disableEdition: boolean
  onClose: () => void
  onIncrease: (additionalId: number) => void
  onSetAmount: (additionalId: number, amount: number) => void
}

export const AdditionalSelectionModal: React.FC<AdditionalSelectionModalProps> = ({
  listCards,
  selectedAdditionals,
  isStockLimitReached,
  disableEdition,
  onClose,
  onIncrease,
  onSetAmount,
}) => {
  const { t } = useTranslation()

  const [additionalFilter, setAdditionalFilter] = useState("")

  const availableAdditionals = useMemo(
    () =>
      listCards.filter((item) => {
        const searchValue = additionalFilter.trim().toUpperCase()

        if (searchValue.length === 0) {
          return true
        }

        return [item.title, ...(item.dataFilter ?? [])].some((value) => value.toUpperCase().includes(searchValue))
      }),
    [additionalFilter, listCards],
  )

  const getAdditionalAmount = (additionalId: number) =>
    selectedAdditionals
      .filter((item) => item.addtionalEntityId === additionalId)
      .reduce((sum, item) => sum + Math.max(0, item.amount), 0)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <div className={defaultStyle.alert1}>
          <TitleChildrenPath
            title={t("order:title_select_additionals")}
            icon={faPlusSquare}
            iconColor={THEME.colors.iconBGSecondaryColor}
            subtitleChildren={
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faRemove}
                onClick={onClose}
                size="small"
                descriptionKey="interactionDescriptions:components.elementOrder.additionalSelectionModal.button.onClose"
              />
            }
            children={
              <div className="flex flex-col gap-3 p-2">
                <InputField
                  icon={faPlusSquare}
                  value={additionalFilter}
                  type="text"
                  placeholder={t("order:label_search_additional")}
                  onChange={(event) => setAdditionalFilter(event.target.value)}
                  descriptionKey="interactionDescriptions:components.elementOrder.additionalSelectionModal.input.searchAdditional"
                />

                <div className="flex items-center justify-between gap-2 rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs font-bold text-orange-900 max-[700px]:flex-col max-[700px]:items-start">
                  <span>{t("order:label_selected_additionals_count", { count: selectedAdditionals.length })}</span>
                  <span>{t("order:label_select_multiple_additionals_hint")}</span>
                </div>

                {availableAdditionals.length === 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center text-xs font-bold text-slate-600">
                    {t("list:label_addtional_page_message_empty")}
                  </div>
                )}

                <div className="grid max-h-[60dvh] grid-cols-1 gap-2 overflow-y-auto pr-1 md:grid-cols-2">
                  {availableAdditionals.map((item) => {
                    const additionalEntity = addtionalMemory.findById(item.id)
                    const amount = getAdditionalAmount(item.id)
                    const value = Number(additionalEntity?.value ?? 0)
                    const isInactive = additionalEntity?.status !== EEnableStatusType.Active
                    const stockLimitReached = isStockLimitReached(item.id, amount)

                    return (
                      <div
                        key={item.id}
                        className={`rounded-lg border p-3 transition ${
                          amount > 0
                            ? "border-emerald-500 bg-emerald-50 shadow-[0_0_0_2px_#a7f3d0]"
                            : "border-orange-100 bg-white"
                        } ${isInactive ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-black text-slate-900">{item.title}</p>
                            <p className="text-xs font-bold text-orange-700">{value.toString().toMoney()}</p>
                          </div>

                          <div className="flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                            <button
                              type="button"
                              disabled={disableEdition || amount <= 0}
                              onClick={() => onSetAmount(item.id, amount - 1)}
                              className="flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                              data-interaction-description-key="interactionDescriptions:components.elementOrder.additionalSelectionModal.button.onSetAmountItemIdAmount1"
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>

                            <span className="w-8 text-center text-sm font-black tabular-nums text-slate-900">
                              {amount}
                            </span>

                            <button
                              type="button"
                              disabled={disableEdition || isInactive || stockLimitReached}
                              onClick={() => onIncrease(item.id)}
                              className="flex h-8 w-8 items-center justify-center rounded-md bg-orange-600 text-white shadow-sm transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-40"
                              data-interaction-description-key="interactionDescriptions:components.elementOrder.additionalSelectionModal.button.onIncreaseItemId"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            }
          />
        </div>
      </div>
    </ModalPortal>
  )
}
